"use client";
import { contactInfo, contactObj } from "@/lib/contacts";
import { useTranslations } from "next-intl";
import React from "react";
import ContactForm from "../InquryForm/ContactForm";
import clsx from "clsx";
import { Link } from "@/navigation";
import CommonTaile from "../Common/CommonTaile";

export default function ConcatPage({ locale }: { locale: string }) {
	const t = useTranslations("nav");

	const breadcrumbItems = [
		{ label: t("Home"), link: "/" },
		{ label: t("Contact Us"), link: "" }
	];

	const navigation = {
		contact: [
			{ title: t("Phone"), value: `${contactInfo.phone}` },
			{ title: t("Email"), value: `${contactInfo.email}` },
			{ title: t("WhatsApp"), value: `${contactInfo.whatsapp}` },
			{ title: t("Wechat"), value: `${contactInfo.wechat}` },
			{ title: t("Address"), value: `${contactInfo.address}` }
		],
	};

	const items = [
		{ icon: "ri-facebook-fill", text: "Facebook", link: contactObj.FaceBook },
		{ icon: "ri-instagram-line", text: "Instagram", link: contactObj.instagram },
		{ icon: "ri-youtube-fill", text: "Youtube", link: contactObj.Youtube },
		{ icon: "ri-linkedin-fill", text: "Linkedin", link: contactObj.Linkedin }
	];
	return (
		<div className="">
			<CommonTaile msg={t("Contact Us")} breadcrumbItems={breadcrumbItems} backgroundImage="/image/plsyy/contact-banner.webp" pageSlug="contact-us" />

			<div className="w-full bg-white">
				<div className="mx-auto grid container grid-cols-1 py-20 text-[28px] max-md:py-8 lg:grid-cols-2 gap-0 items-stretch">
					<div className="p-6 md:p-10 bg-mainColor rounded-2xl lg:rounded-tl-2xl lg:rounded-bl-2xl lg:rounded-tr-none lg:rounded-br-none max-lg:mb-6 shadow-xl flex flex-col lg:h-full relative overflow-hidden">
						{/* 背景装饰 */}
						<div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 max-md:hidden"></div>
						<div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2 max-md:hidden"></div>
						
						<div className="relative z-10 flex flex-col lg:h-full">
							{/* 第一部分：标题+副标题 */}
							<div className="lg:flex-1 lg:flex lg:flex-col lg:justify-center mb-6 lg:mb-8 pb-6 lg:pb-8 border-b border-white/10">
								<h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-3 lg:mb-4 tracking-tight">{t("Contact Us")}</h2>
								<p className="text-white/85 text-sm md:text-[15px] leading-relaxed">
									{t("Contact Us Description")}
								</p>
							</div>

							{/* 第二部分：联系方式 */}
							<div className="lg:flex-1 lg:flex lg:flex-col lg:justify-center mb-6 lg:mb-8 pb-6 lg:pb-8 border-b border-white/10">
								<ul role="list" className="flex flex-col space-y-3 md:space-y-4 lg:space-y-5">
									{navigation.contact.map((item, index) => (
										<li key={index} className="flex items-start gap-3 md:gap-4 group">
											<div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center shadow-lg group-hover:bg-white/25 transition-all duration-300 group-hover:scale-110">
												<i className={clsx(
													index === 0 && "ri-phone-fill",
													index === 1 && "ri-mail-fill",
													index === 2 && "ri-whatsapp-fill",
													index === 3 && "ri-wechat-fill",
													index === 4 && "ri-map-pin-fill",
													"text-white text-lg md:text-xl"
												)}></i>
											</div>
											<div className="flex-1 pt-1">
												<h3 className="text-sm md:text-base font-semibold text-white mb-1 md:mb-1.5">{item.title}</h3>
												<div className="text-xs md:text-[14px] text-white/80 leading-relaxed break-words">{item.value}</div>
											</div>
										</li>
									))}
								</ul>
							</div>

							{/* 第三部分：营业时间 */}
							<div className="lg:flex-1 lg:flex lg:flex-col lg:justify-center mb-6 lg:mb-8 pb-6 lg:pb-8 border-b border-white/10">
								<h3 className="text-base md:text-lg font-semibold text-white mb-3 md:mb-5">{t("Business Hours")}</h3>
								<ul className="space-y-2 md:space-y-3">
									<li className="flex items-center gap-2 md:gap-3">
										<div className="w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-white shadow-sm flex-shrink-0"></div>
										<span className="text-xs md:text-[15px] text-white/85">{t("Monday-Friday")}: 9:00 AM - 6:00 PM</span>
									</li>
									<li className="flex items-center gap-2 md:gap-3">
										<div className="w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-white shadow-sm flex-shrink-0"></div>
										<span className="text-xs md:text-[15px] text-white/85">{t("Saturday")}: 10:00 AM - 4:00 PM</span>
									</li>
									<li className="flex items-center gap-2 md:gap-3">
										<div className="w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-white shadow-sm flex-shrink-0"></div>
										<span className="text-xs md:text-[15px] text-white/85">{t("Sunday")}: {t("Closed")}</span>
									</li>
								</ul>
							</div>

							{/* 第四部分：社交媒体 */}
							<div className="lg:flex-1 lg:flex lg:flex-col lg:justify-center">
								<h3 className="text-base md:text-lg font-semibold text-white mb-3 md:mb-5">{t("Follow Us")}</h3>
								<div className="flex gap-3 md:gap-4 lg:gap-5">
									{items.map((item, index) => (
										<Link 
											key={index} 
											href={item.link} 
											target="_blank" 
											className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-white/15 backdrop-blur-sm hover:bg-white flex items-center justify-center transition-all duration-300 hover:scale-110 group shadow-lg hover:shadow-xl"
										>
											<i key={item.text} className={clsx(item.icon, "text-xl md:text-2xl text-white group-hover:text-mainColor transition-colors")}></i>
										</Link>
									))}
								</div>
							</div>
						</div>
					</div>
					<div className="p-6 md:p-10 bg-white rounded-2xl lg:rounded-tr-2xl lg:rounded-br-2xl lg:rounded-tl-none lg:rounded-bl-none shadow-lg border border-gray-200 lg:h-full flex flex-col">
						<h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-black mb-3">{t("Contact Card")}</h2>
						<p className="text-gray-600 text-sm md:text-base mb-6 md:mb-8 leading-relaxed border-b border-mainColor/30 pb-6 md:pb-8">
							{t("Contact Card Description")}
						</p>
						<div className="flex-1">
							<ContactForm
								locale={locale}
								title={""} // 去掉表单内部重复的标题
								// @ts-ignore
								titleContent={""} // 去掉表单内部重复的描述
								className="!py-0 !px-0" // 强制去掉表单内部边距
								innerClassName="!shadow-none !rounded-none !p-0 !bg-transparent" // 去掉内部背景和阴影，使其完全融入父容器
							></ContactForm>
						</div>
					</div>
				</div>
			</div>
			
			<div className="w-full bg-[#F2F4F5]">
				<div className="container mx-auto py-10 md:py-20">
					<div className="text-center mb-6 md:mb-8">
						<h2 className="text-2xl md:text-3xl lg:text-4xl font-bold">{t('MapAndDirections')}</h2>
						<p className="text-sm md:text-base lg:text-lg mt-3 md:mt-4">
							{t('mapTip')}
						</p>
					</div>
					<div className="w-full overflow-hidden shadow-lg">
						<iframe
							src={`https://www.google.com/maps?q=${encodeURIComponent(contactInfo.address)}&output=embed`}
							width="100%"
							height="400"
							className="md:h-[500px] lg:h-[650px]"
							loading="lazy"
							referrerPolicy="no-referrer-when-downgrade"
							style={{ border: 0 }}
							title="Blince Machinery Location"
						></iframe>
					</div>
				</div>
			</div>
		</div>
	);
}
